import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { downloadFile } from "@shared/utils/utils";
import { axServiceOrderRegistry } from "./api";
import {
  CreateServiceOrderResponse,
  JobCardData,
  ServiceOrderFilters,
} from "./types";

export const useServiceOrdersList = (
  searchData?: string,
  status?: string,
  priority?: string,
  sopAssociationType?: string,
  fieldForceName?: string,
  page?: number,
  limit?: number
) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrder.list(
      remoteUtilityId,
      page,
      limit,
      searchData,
      status,
      priority,
      sopAssociationType,
      fieldForceName
    ),
    () =>
      axServiceOrderRegistry.getServiceOrders(
        remoteUtilityId,
        page,
        limit,
        searchData,
        status,
        priority,
        sopAssociationType,
        fieldForceName
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useServiceOrderSummary = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrder.summary(
      remoteUtilityId
    ),
    () => axServiceOrderRegistry.getServiceOrderSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};
// Add to your existing hooks
export const useServiceOrderHistorySummary = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrderHistory.summary(
      remoteUtilityId
    ),
    () => axServiceOrderRegistry.getServiceOrderHistorySummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};
export const useServiceOrderHistoryList = (
  searchData?: string,
  status?: string,
  priority?: string,
  orderType?: string,
  assignedTo?: string,
  page?: number,
  limit?: number
) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrderHistory.list(
      remoteUtilityId,
      page,
      limit,
      searchData,
      status,
      priority,
      orderType,
      assignedTo
    ),
    () =>
      axServiceOrderRegistry.getServiceOrderHistory(
        remoteUtilityId,
        page,
        limit,
        searchData,
        status,
        priority,
        orderType,
        assignedTo
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useSOPList = (
  searchData?: string,
  sopAssociationType?: string,
  operationType?: string,
  page?: number,
  limit?: number
) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.sopTemplate.list(
      remoteUtilityId,
      page,
      limit,
      searchData,
      sopAssociationType,
      operationType
    ),
    () =>
      axServiceOrderRegistry.getSOPList(
        remoteUtilityId,
        page,
        limit,
        searchData,
        sopAssociationType,
        operationType
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useServiceOrderDetails = (serviceOrderId?: string) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrder.details(
      remoteUtilityId,
      serviceOrderId || ""
    ),
    () =>
      axServiceOrderRegistry.getServiceOrderDetails(
        remoteUtilityId,
        serviceOrderId || ""
      ),
    {
      enabled: !!remoteUtilityId && !!serviceOrderId,
    }
  );
};

//
export const useServiceOrderAssets = (
  searchData?: string,
  type?: string,
  system?: string,
  facility?: string,
  network?: string,
  area?: string,
  page?: number,
  limit?: number,
  serviceAssociation?: string
) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrderAssets.list(
      remoteUtilityId,
      page,
      limit,
      searchData,
      type,
      area,
      system,
      network,
      facility
    ),
    () =>
      axServiceOrderRegistry.getServiceOrderAssets(
        remoteUtilityId,
        page,
        limit,
        searchData,
        type,
        area,
        system,
        network,
        facility
      ),
    {
      enabled: !!remoteUtilityId && serviceAssociation === "Asset",
    }
  );
};

export const useServiceOrderSystems = (
  searchData?: string,
  systemType?: string,
  area?: string,
  page?: number,
  limit?: number,
  serviceAssociation?: string,
  facility?: string
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrderSystems.list(
      remoteUtilityId,
      page,
      limit,
      searchData,
      systemType,
      area,
      facility
    ),
    () =>
      axServiceOrderRegistry.getServiceOrderSystems(
        remoteUtilityId,
        page,
        limit,
        searchData,
        systemType,
        area,
        facility
      ),
    {
      enabled: !!remoteUtilityId && serviceAssociation === "System",
    }
  );
};

export const useServiceOrderFacilities = (
  searchData?: string,
  facilityType?: string,
  area?: string,
  page?: number,
  limit?: number,
  serviceAssociation?: string
) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrderFacilities.list(
      remoteUtilityId,
      page,
      limit,
      searchData,
      facilityType,
      area
    ),
    () =>
      axServiceOrderRegistry.getServiceOrderFacilities(
        remoteUtilityId,
        page,
        limit,
        searchData,
        facilityType,
        area
      ),
    {
      enabled: !!remoteUtilityId && serviceAssociation === "Facility",
    }
  );
};

export const useServiceOrderNetworks = (
  searchData?: string,
  networkType?: string,
  area?: string,
  page?: number,
  limit?: number,
  serviceAssociation?: string,
  facility?: string
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrderNetworks.list(
      remoteUtilityId,
      page,
      limit,
      searchData,
      networkType,
      area,
      facility
    ),
    () =>
      axServiceOrderRegistry.getServiceOrderNetworks(
        remoteUtilityId,
        page,
        limit,
        searchData,
        networkType,
        area,
        facility
      ),
    {
      enabled: !!remoteUtilityId && serviceAssociation === "Network",
    }
  );
};
export const useCreateServiceOrder = () => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  const filters: ServiceOrderFilters = {};

  return useSmartMutation(
    (jobCardData: JobCardData[]): Promise<CreateServiceOrderResponse> => {
      return axServiceOrderRegistry.createServiceOrder(
        remoteUtilityId,
        jobCardData
      );
    },
    {
      onSuccess: async (data) => {
        toast({
          title: "Success!",
          description: data.message,
        });

        // Invalidate and refetch related queries
        queryClient.refetchQueries({
          queryKey: [
            "ax",
            "serviceOrders",
            "list",
            remoteUtilityId,
            1,
            50,
            "",
            "",
            "",
            "",
            "",
          ],
        });

        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.assets.oAndM().slice(0, 4),
        });

        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemManagement
            .getAllServiceOrder(Number(remoteUtilityId))
            .slice(0, 4),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.facilities.serviceOrders
            .list(remoteUtilityId)
            .slice(0, 4),
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create service orders",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateServiceOrder = () => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    ({
      id,
      jobCardData,
    }: {
      id: number;
      jobCardData: JobCardData[];
    }): Promise<CreateServiceOrderResponse> => {
      return axServiceOrderRegistry.updateServiceOrder(
        remoteUtilityId,
        id,
        jobCardData
      );
    },
    {
      onSuccess: (data) => {
        toast({
          title: "Success!",
          description: data.message,
        });

        // Invalidate and refetch related queries
        queryClient.refetchQueries({
          queryKey: [
            "ax",
            "serviceOrders",
            "list",
            remoteUtilityId,
            1,
            50,
            "",
            "",
            "",
            "",
            "",
          ],
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update service order",
          variant: "destructive",
        });
      },
    }
  );
};

export const useCancelServiceOrders = () => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    (jobCardIds: number[]): Promise<any> => {
      return axServiceOrderRegistry.cancelServiceOrders(
        remoteUtilityId,
        jobCardIds
      );
    },
    {
      onSuccess: (data, variables) => {
        toast({
          title: "Success",
          description: "Service orders deleted successfully",
          variant: "default",
        });

        // Invalidate and refetch related queries
        queryClient.invalidateQueries({
          queryKey: ["ax", "serviceOrders"],
        });

        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.ax.serviceAndMangement.serviceOrder.summary(
              remoteUtilityId
            ),
        });

        queryClient.refetchQueries({
          queryKey: ["ax", "serviceOrders", "list"],
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to cancel service orders",
          variant: "destructive",
        });
      },
    }
  );
};

export const useServiceOrderDetail = (id: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrderDetails.detail(
      id
    ),
    () => axServiceOrderRegistry.getServiceOrderDetail(id),
    {
      enabled: !!id,
    }
  );
};

export const useGetAllOperationType = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.serviceAndMangement.serviceOrder.operationType(),
    () => axServiceOrderRegistry.getAllOperationType(),
    {
      enabled: true,
    }
  );
};

export const useDownloadReport = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    async ({
      reportId,
      filename = "report.pdf", // or whatever default extension you expect
      tabName,
    }: {
      reportId: number;
      filename?: string;
      tabName?: string;
    }) => {
      const blob = await axServiceOrderRegistry.downloadReport(
        reportId,
        remoteUtilityId,
        tabName
      );
      downloadFile(blob, filename);
      return blob;
    }
  );
};
